import React, { useEffect, useState } from "react";
import YhInput from "../../../../../../ui/YhInput";
import YhSelect from "../../../../../../ui/YhSelect";
import YhButton from "../../../../../../ui/YhButton";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import {
  addProjectFilterMeta,
  getProjectFilterFieldValues,
  getProjectFilterMeta,
} from "./Service";
import { ImageUpLoad } from "../../../../../Team/components/stepper/EmployeeDocuments/EmployeeDocumentServices";

const AMENITY_CATEGORIES = [
  "Sports",
  "Convenience",
  "Safety",
  "Leisure",
  "Environment",
].map((c) => ({ label: c, value: c }));

const FILTER_FIELD_OPTIONS = [
  { label: "Property Purpose", value: "propertyPurpose" },
  { label: "Property Type", value: "propertyType" },
  { label: "Building Type", value: "buildingType" },
  { label: "Tags", value: "tags" },
  { label: "Property Status", value: "propertyStatus" },
  { label: "All projects (no field filter)", value: "all" },
];

const UI_TYPE_OPTIONS = [
  { label: "Standard listing", value: "default" },
  { label: "Projects gallery", value: "projects" },
];

const TAB_FIELD_MAP: Record<string, string> = {
  propertyPurpose: "propertyPurpose",
  buildingType: "buildingType",
  propertyType: "propertyType",
  bedrooms: "bedrooms",
  priceRange: "priceRange",
  propertyStatus: "propertyStatus",
  propertySaleType: "propertySaleType",
  expectedPossession: "expectedPossession",
  furnishingType: "furnishingType",
  highlights: "highlights",
  specifications: "specifications",
};

interface MetaDrawerProps {
  currentTabKey: string;
  currentTabLabel: string;
  selectedRow: any;
  search?: string;
  closeDrawer: () => void;
}

const MetaDrawerComponent: React.FC<MetaDrawerProps> = ({
  currentTabKey,
  currentTabLabel,
  selectedRow,
  search = "",
  closeDrawer,
}) => {
  const [formData, setFormData] = useState<any>({});
  const [metaDocId, setMetaDocId] = useState<string | null>(null);
  const [property, setProperty] = useState<any[]>([]);
  const [fieldValues, setFieldValues] = useState<string[]>([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isEdit = !!(selectedRow && selectedRow._id);
  const isPurposeTab = currentTabKey === "propertyPurpose";
  const isAmenityTab = currentTabKey === "amenities";
  const isProjectsUi = formData.uiType === "projects";
  const valueField = isPurposeTab
    ? formData.filterField || "propertyPurpose"
    : TAB_FIELD_MAP[currentTabKey] || currentTabKey;

  useEffect(() => {
    fetchMetaData();
  }, []);

  useEffect(() => {
    if (selectedRow && selectedRow._id) {
      setFormData({
        status: true,
        showOnWebsite: true,
        uiType: "default",
        ...selectedRow,
        filterField:
          selectedRow.filterField ||
          (isPurposeTab ? "propertyPurpose" : TAB_FIELD_MAP[currentTabKey]),
        filterValue: selectedRow.filterValue || selectedRow.name || "",
      });
    } else {
      setFormData({
        status: true,
        showOnWebsite: true,
        uiType: "default",
        filterField: isPurposeTab
          ? "propertyPurpose"
          : TAB_FIELD_MAP[currentTabKey] || "",
      });
    }
  }, [selectedRow, currentTabKey, isPurposeTab]);

  useEffect(() => {
    if (isAmenityTab) return;
    if (isProjectsUi || valueField === "all") {
      setFieldValues([]);
      return;
    }
    let cancelled = false;
    const loadValues = async () => {
      try {
        setLoadingValues(true);
        const res = await getProjectFilterFieldValues(valueField);
        const values = res?.data?.values || res?.data || [];
        if (!cancelled) {
          setFieldValues(Array.isArray(values) ? values : []);
        }
      } catch (error) {
        console.error("Error fetching field values", error);
        if (!cancelled) setFieldValues([]);
      } finally {
        if (!cancelled) setLoadingValues(false);
      }
    };
    loadValues();
    return () => {
      cancelled = true;
    };
  }, [valueField, isAmenityTab, isProjectsUi]);

  const fetchMetaData = async () => {
    try {
      const response = await getProjectFilterMeta(search);
      if (response?.data?.length > 0) {
        const latest = response.data[response.data.length - 1];
        setProperty(latest.propertyPurpose || []);
        setMetaDocId(latest._id);
      }
    } catch (error) {
      console.error("Error fetching metadata", error);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const data = new FormData();
      data.append("file", file);
      const uploadedUrl = await ImageUpLoad(data);
      setFormData((prev: any) => ({
        ...prev,
        icon: uploadedUrl.imageUrl,
        status: true,
      }));
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    const displayName = String(formData.name || "").trim();
    if (!displayName) {
      openErrorNotification("Select a filter name from project data");
      return;
    }
    if (isAmenityTab && !formData.categoryName) {
      openErrorNotification("Select an amenity category");
      return;
    }
    if (
      !isAmenityTab &&
      !isProjectsUi &&
      valueField !== "all" &&
      !String(formData.filterValue || "").trim()
    ) {
      openErrorNotification("Select a value that already exists on projects");
      return;
    }

    try {
      setSubmitting(true);
      const response = await getProjectFilterMeta();
      let existingArray: any[] = [];
      let docId = metaDocId;

      if (response?.data?.length > 0) {
        const latest = response.data[response.data.length - 1];
        docId = latest._id;
        setMetaDocId(latest._id);
        existingArray = latest[currentTabKey] || [];
      }

      const nextItem = {
        ...formData,
        name: displayName,
        status: formData.status !== false,
        filterField: isAmenityTab
          ? undefined
          : isProjectsUi
            ? "all"
            : valueField,
        filterValue: isProjectsUi
          ? ""
          : currentTabKey === "priceRange"
            ? String(formData.filterValue || displayName).trim()
            : String(formData.filterValue || displayName).trim(),
        uiType: isPurposeTab ? formData.uiType || "default" : undefined,
        showOnWebsite: isPurposeTab ? formData.showOnWebsite !== false : undefined,
      };

      if (currentTabKey === "priceRange") {
        const amount = Number(String(nextItem.filterValue).replace(/[^\d.]/g, ""));
        const isCrore = /cr|crore/i.test(String(nextItem.name || nextItem.filterValue));
        const isLakh = /lakh|lac/i.test(String(nextItem.name || nextItem.filterValue));
        if (isCrore && amount < 100000) nextItem.filterValue = String(Math.round(amount * 10000000));
        else if (isLakh && amount < 10000000) nextItem.filterValue = String(Math.round(amount * 100000));
        else if (Number.isFinite(amount) && amount > 0) nextItem.filterValue = String(Math.round(amount));
      }

      let updatedArray: any[] = [];

      if (isEdit) {
        updatedArray = existingArray.map((item: any) =>
          item._id === selectedRow._id
            ? { ...item, ...nextItem, _id: selectedRow._id }
            : item
        );
      } else {
        updatedArray = [...existingArray, { ...nextItem, status: true }];
      }

      const payload = {
        _id: docId,
        [currentTabKey]: updatedArray,
      };

      const addResponse = await addProjectFilterMeta(payload);
      if (addResponse) {
        openSuccessNotification(addResponse?.message || "Saved successfully");
        closeDrawer();
      }
    } catch (error) {
      console.error("Error submitting filter meta:", error);
      openErrorNotification("Could not save this filter");
    } finally {
      setSubmitting(false);
    }
  };

  const propertyOptions = (property || []).map((opt: any) => ({
    label: opt?.name || "",
    value: opt?.name || "",
  }));

  const nameOptions = [
    ...fieldValues.map((value) => ({ label: value, value })),
    ...(formData.name && !fieldValues.includes(formData.name)
      ? [{ label: formData.name, value: formData.name }]
      : []),
  ];

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
      onClick={closeDrawer}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
          <h2 className="text-lg font-bold text-[#111827]">
            {isEdit ? `Edit ${currentTabLabel}` : `Create ${currentTabLabel}`}
          </h2>
          <button
            type="button"
            onClick={closeDrawer}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <p className="mb-4 rounded-lg bg-[#F8FAFC] px-3 py-2 text-xs text-[#6B7280]">
            Names are loaded from this organization&apos;s projects in the
            database. Only existing values can be added as filters.
          </p>
          <div className="grid grid-cols-1 gap-4">
            {isAmenityTab ? (
              <YhSelect
                label="Category"
                placeholder="Select category"
                value={formData.categoryName || ""}
                options={AMENITY_CATEGORIES}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    categoryName: e.target.value,
                    status: true,
                  })
                }
              />
            ) : null}

            {isPurposeTab ? (
              <>
                <YhSelect
                  label="Filter field"
                  requiredMark
                  placeholder="Select project field"
                  value={formData.filterField || "propertyPurpose"}
                  options={FILTER_FIELD_OPTIONS}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      filterField: e.target.value,
                      filterValue: "",
                      name: isProjectsUi ? formData.name : "",
                      status: true,
                    })
                  }
                />
                <YhSelect
                  label="Website UI"
                  value={formData.uiType || "default"}
                  options={UI_TYPE_OPTIONS}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      uiType: e.target.value,
                      filterField:
                        e.target.value === "projects"
                          ? "all"
                          : formData.filterField || "propertyPurpose",
                      name:
                        e.target.value === "projects"
                          ? formData.name || "Projects"
                          : formData.name,
                      status: true,
                    })
                  }
                />
              </>
            ) : null}

            {!isAmenityTab && currentTabKey !== "propertyPurpose" ? (
              <YhSelect
                label="Property Purpose"
                placeholder="Select"
                value={formData.propertyPurpose || ""}
                options={propertyOptions}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    propertyPurpose: e.target.value,
                    status: true,
                  })
                }
              />
            ) : null}

            {isAmenityTab || isProjectsUi ? (
              <YhInput
                label={isProjectsUi ? "Tab name" : "Name"}
                requiredMark
                placeholder={isProjectsUi ? "Projects" : "Enter name"}
                value={formData.name ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    status: true,
                  })
                }
              />
            ) : (
              <YhSelect
                label="Filter name"
                requiredMark
                searchable
                placeholder={
                  loadingValues
                    ? "Loading project values…"
                    : fieldValues.length
                      ? "Select a value from projects"
                      : "No matching values in this org's projects"
                }
                value={formData.filterValue || formData.name || ""}
                options={nameOptions}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    filterValue: e.target.value,
                    name: isPurposeTab
                      ? formData.name || e.target.value
                      : e.target.value,
                    status: true,
                  })
                }
              />
            )}

            {isPurposeTab && !isProjectsUi ? (
              <YhInput
                label="Tab label"
                placeholder="Same as filter name"
                value={formData.name ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    status: true,
                  })
                }
              />
            ) : null}

            {isPurposeTab ? (
              <label className="flex items-center gap-3 text-sm text-[#374151]">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#A02448]"
                  checked={formData.showOnWebsite !== false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      showOnWebsite: e.target.checked,
                      status: true,
                    })
                  }
                />
                Show this tab on the public website search
              </label>
            ) : null}

            <div>
              <label className="yh-label">Icon</label>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[#D1D5DB] bg-[#F9FAFB] px-4 py-8 text-center hover:border-[#A02448] hover:bg-[#F5F6FF]">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#A02448"
                  strokeWidth="1.5"
                  className="mb-2"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <path d="M17 8l-5-5-5 5" />
                  <path d="M12 3v12" />
                </svg>
                <span className="text-sm text-[#6B7280]">
                  {uploading
                    ? "Uploading…"
                    : "Drag and drop or click to upload"}
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </label>

              {formData.icon ? (
                <div className="relative mt-3 inline-block h-24 w-24 overflow-hidden rounded-lg border border-[#EEF0F5]">
                  <img
                    src={formData.icon}
                    alt="Icon"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, icon: "" })
                    }
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                    aria-label="Remove image"
                  >
                    ✕
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-[#EEF0F5] bg-[#FAFBFC] px-5 py-4">
          <YhButton variant="secondary" size="sm" onClick={closeDrawer}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting || uploading}
          >
            {submitting ? "Saving…" : "Submit"}
          </YhButton>
        </div>
      </div>
    </div>
  );
};

export default MetaDrawerComponent;
